//@flow
import React from "react";
import styled, { css } from "styled-components";
import { withState, compose, pure, lifecycle, mapProps } from "recompose";
import range from "lodash.range";
import getResizedUrl from "./getResizedUrl";
// import LoadingAnim from "../LoadingAnim";

const viewPortRange = range(100, 3300, 100);

const getSrcSet = ({ width, url }) =>
  viewPortRange
    .filter((w, idx) => {
      if (!width || idx === 0) {
        return true;
      }
      return width * 2 > w;
    })
    .map(w => `${getResizedUrl({ width: w, url })} ${w}w`)
    .join(", ");

const isSrcSetSupported = () => {
  if (typeof window === "undefined") {
    return true;
  }
  const img = new Image();
  return "sizes" in img;
};

const withSrcSet = mapProps(
  ({ source = "", width, ...props }) =>
    isSrcSetSupported() &&
    source !== "" &&
    !source.includes("localhost") &&
    !props.noResize
      ? {
          ...props,
          srcSet: getSrcSet({ width, url: source })
        }
      : { ...props, source, width }
);

const loadImage = ({ source, src, srcSet, sizes }): Promise =>
  new Promise((resolve, reject) => {
    if ((!srcSet && !src) || typeof window === "undefined") {
      resolve();
    }
    const img = new Image();
    img.onload = () => resolve({ srcSet, sizes });
    img.onerror = () => reject({ srcSet, sizes });
    if (srcSet) {
      img.sizes = sizes;
      img.srcset = srcSet;
    } else {
      img.src = source;
    }
  });

const makeCancelable = promise => {
  let hasCanceled_ = false;

  const wrappedPromise = new Promise((resolve, reject) => {
    promise.then(
      val => (hasCanceled_ ? reject({ isCanceled: true }) : resolve(val)),
      error => (hasCanceled_ ? reject({ isCanceled: true }) : reject(error))
    );
  });

  return {
    promise: wrappedPromise,
    cancel() {
      hasCanceled_ = true;
    }
  };
};

const imagePreloader = compose(
  withState("loaded", "toggleLoaded", false),
  withState("loading", "setLoading", false),
  lifecycle({
    componentWillMount() {
      if (typeof Image !== "undefined") {
        this.timeout = setTimeout(
          () => !this.props.loaded && this.props.setLoading(true),
          1500
        );
        this.loadImageP = makeCancelable(loadImage(this.props));
        this.loadImageP.promise
          .then(() => {
            this.props.toggleLoaded(true);
            this.props.setLoading(false);
            if (this.props.handleLoaded) {
              this.props.handleLoaded();
            }
          })
          .catch(e => {
            if (!e.isCanceled) {
              console.log("Error imagePreloader", { e });
            }
            this.props.setSource(this.props.fallbackSrc);
          });
      }
    },
    componentWillUnmount() {
      if (this.timeout) clearTimeout(this.timeout);
      this.loadImageP.cancel();
    }
  })
);

const loadedStyles = css`
  opacity: 1;
`;

const unloadedStyles = css`
  opacity: 0;
`;

const PreloadedImage = styled.div`
  transition: opacity 0.3s;
  ${props => (props.loaded ? loadedStyles : unloadedStyles)};
  background-image: url(${props => props.backgroundImage});
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
`;

const enhance = compose(
  withState("source", "setSource", props => props.src),
  withSrcSet,
  imagePreloader,
  pure
);

const CustomImage = ({ loading, className, ...props }) => (
  <PreloadedImage className={className} {...props} />
);

const Container = styled.div`
  width: 100%;
  position: relative;
  height: 100%;
`;
// const Loading = styled(LoadingAnim)`
//   position: absolute;
//   top: 0;
//   left: 0;
//   width: 100%;
//   height: 100%;
// `;

export default enhance(PreloadedImage);
